import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { auth, db, functions, googleProvider, httpsCallable, onAuthStateChanged } from "../firebase";
import { Capacitor } from "@capacitor/core";
import {
  User,
  signInAnonymously,
  signInWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
  linkWithCredential,
  linkWithPopup,
} from "firebase/auth";
import {
  collection,
  doc as firestoreDoc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  setDoc,
  addDoc,
  serverTimestamp,
  increment,
  DocumentReference,
} from "firebase/firestore";
import { UserDetails, UserContextValue, OnboardingData, LoginRewards, DEFAULT_NOTIFICATION_PREFS } from "../types";
import { isLocalhost, getDevAccountReason } from "../utils/isLocalhost";
import { getInstallSourceForUserDoc } from "../utils/installSource";

interface UserContextProps {
  children: ReactNode;
}

const UserContext = createContext<UserContextValue | null>(null);

/** Recursively remove keys whose values are `undefined`. Firestore writes
 *  reject `undefined` and throw a runtime error, but our types use optional
 *  fields liberally — so callers commonly hand us objects with `undefined`
 *  scattered through nested structures (e.g. `preferences.gender`). Arrays
 *  pass through untouched (sparse arrays are rare here and undefined slots
 *  would be a different bug). `null` is preserved — the user may explicitly
 *  want to clear a field. */
function stripUndefined<T>(value: T): T {
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(stripUndefined) as unknown as T;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (v === undefined) continue;
    out[k] = stripUndefined(v);
  }
  return out as T;
}

/** Call captureSignupGeo after a fresh user doc is committed. The cloud
 *  function looks up the user by `where("userId", "==", uid)`, which can
 *  briefly miss the just-written doc due to query-index propagation lag.
 *  We delay the first call by 1.5s to give the index time to update, and
 *  retry once at +4s if the function reports the user wasn't found. */
function triggerGeoCaptureWithRetry(): void {
  const call = () => httpsCallable(functions, "captureSignupGeo")();
  const handleNotFound = (res: { data: unknown }): void => {
    const data = res.data as { skipped?: boolean; reason?: string } | null;
    if (data?.skipped && data.reason === "user-not-found") {
      setTimeout(() => { call().catch(() => { /* give up */ }); }, 4000);
    }
  };
  setTimeout(() => {
    call()
      .then(handleNotFound)
      .catch(() => { /* refresh on next session will retry implicitly */ });
  }, 1500);
}

export const UserProvider: React.FC<UserContextProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const userDocRef = useRef<DocumentReference | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      // Only clear authLoading immediately when there's NO user.
      // When there IS a user, authLoading is cleared after refreshUserDetails
      // finishes fetching the full profile from Firestore.
      if (!currentUser) {
        setAuthLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const refreshUserDetails = async (overrideUser?: User) => {
    const targetUser = overrideUser || user;
    if (!targetUser) {
      setUserDetails(null);
      localStorage.removeItem("newUserState");
      return;
    }

    try {
      const dbCol = collection(db, "users");
      const q = query(dbCol, where("userId", "==", targetUser.uid));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        const docRef = doc.ref;
        userDocRef.current = docRef;

        const extendedUser = { ...targetUser, name: data.name || data.email };

        const storedNewUserState = localStorage.getItem(
          `newUserState_${targetUser.uid}`
        );

        let subscription = null;
        let subscriptionEndingSoon = false;
        let existingSubscription = data.existingSubscription || false;

        if (data.subscription && !existingSubscription) {
          existingSubscription = true;
          await updateDoc(docRef, { existingSubscription: true });
        }

        if (data.subscription) {
          const startDate =
            data?.subscription?.startDate?.toDate() || new Date();
          const endDate = data?.subscription?.endDate?.toDate() || new Date();
          const now = new Date();
          const timeDiff = endDate.getTime() - now.getTime();
          const daysRemaining = timeDiff / (1000 * 60 * 60 * 24);
          const paddedNow = new Date(now.getTime() - 5 * 60 * 1000);

          if (endDate < paddedNow && data.subscription.plan !== "free") {
            await updateDoc(docRef, {
              "subscription.active": false,
              "subscription.plan": "free",
            });

            subscription = {
              ...data.subscription,
              active: false,
              plan: "free",
              startDate,
              endDate,
            };
          } else {
            if (daysRemaining <= 1) {
              subscriptionEndingSoon = true;
            }

            subscription = {
              ...data.subscription,
              startDate,
              endDate,
            };
          }
        }

        const rawDate = data.dateCreated || data.date_created;
        const dateCreatedRaw = rawDate
          ? (rawDate.toDate ? rawDate.toDate() : new Date(rawDate))
          : undefined;

        // Backfill timezone if missing — needed by scheduled functions for accurate
        // local-time notification windows. Cheap browser lookup, fire-and-forget.
        const browserTz = (() => {
          try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return null; }
        })();
        if (browserTz && data.timezone !== browserTz) {
          updateDoc(docRef, { timezone: browserTz }).catch(() => { /* non-critical */ });
        }

        // Capture geo metadata once per user (country, region, IP hash).
        // Skip if we've already got it. Fire-and-forget — not on the critical path.
        if (!data.signupGeo?.country) {
          httpsCallable(functions, "captureSignupGeo")()
            .catch(() => { /* geo lookup failed — try again next session */ });
        }

        setUserDetails({
          user: extendedUser,
          dateCreated: dateCreatedRaw
            ? dateCreatedRaw.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
            : "",
          dateCreatedRaw,
          admin: data.admin || false,
          sharedCalendars: data.sharedCalendars || [],
          familyMembers: data.familyMembers || [],
          familyInvites: data.familyInvites || [],
          tester: data.tester || false,
          subscription,
          subscriptionEndingSoon,
          existingSubscription,
          onboarding: data.onboarding || undefined,
          loginRewards: data.loginRewards ?? undefined,
          referredBy: data.referredBy ?? undefined,
          earlyAdopter: data.earlyAdopter ?? false,
          isBetaTester: data.isBetaTester ?? false,
          timezone: browserTz ?? data.timezone,
          signupGeo: data.signupGeo,
        });

        // Fire-and-forget: record daily activity (used by login rewards + admin insights)
        {
          const today = new Date().toISOString().slice(0, 10);
          const activityRef = firestoreDoc(db, "activityLogs", `${targetUser.uid}_${today}`);
          const isStandalone = window.matchMedia("(display-mode: standalone)").matches
            || window.matchMedia("(display-mode: fullscreen)").matches
            || (navigator as any).standalone === true;
          const platform = (window as any).Capacitor?.isNativePlatform?.()
            ? "android"
            : isStandalone
            ? "pwa"
            : "web";
          setDoc(activityRef, {
            userId: targetUser.uid,
            date: today,
            lastActiveAt: serverTimestamp(),
            loginCount: increment(1),
            platform,
          }, { merge: true }).catch(() => {});
        }

        if (!storedNewUserState) {
          // If user has already completed onboarding, mark them as not new.
          // Only flag as "true" (new) when onboarding hasn't been done yet.
          localStorage.setItem(
            `newUserState_${targetUser.uid}`,
            data.onboarding ? "false" : "true"
          );
          localStorage.removeItem("newUserState");
        } else if (data.onboarding && storedNewUserState === "true") {
          // Existing user with completed onboarding should never be flagged as new.
          localStorage.setItem(`newUserState_${targetUser.uid}`, "false");
        }
      } else {
        setUserDetails({
          user: targetUser,
          dateCreated: "",
          admin: false,
          sharedCalendars: [],
          familyMembers: [],
          familyInvites: [],
          tester: false,
          subscription: null,
          existingSubscription: false,
          earlyAdopter: false,
          isBetaTester: false,
        });
        // Anonymous users reach this branch in the brief window between
        // signInAnonymously completing (which fires onAuthStateChanged and
        // triggers this refresh) and signInAsGuest finishing the user-doc
        // addDoc. They are new and MUST go through onboarding — so flag
        // them as a new user, not a returning one.
        const isNewAnonymous = targetUser.isAnonymous;
        localStorage.setItem(
          `newUserState_${targetUser.uid}`,
          isNewAnonymous ? "true" : "false",
        );
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      setUserDetails(null);
    } finally {
      setAuthLoading(false);
    }
  };

  const saveOnboarding = useCallback(async (data: OnboardingData) => {
    if (!userDocRef.current) return;
    // Strip undefined values recursively — Firestore rejects undefined and
    // throws "Unsupported field value: undefined". Optional preference fields
    // (like `gender`, `age`, `dateOfBirth`) often come through as undefined
    // when the user hasn't filled them in, which used to crash the save.
    const sanitized = stripUndefined(data) as OnboardingData;
    await updateDoc(userDocRef.current, { onboarding: sanitized });
    setUserDetails((prev) => prev ? { ...prev, onboarding: sanitized } : prev);
  }, []);

  /**
   * Sign in anonymously and create a minimal user doc.
   * Used by the welcome screen "Create new account" button to skip the email/password form.
   * If the user is already signed in (anonymous or otherwise), this is a no-op.
   */
  const signInAsGuest = useCallback(async () => {
    if (auth.currentUser) return;
    const cred = await signInAnonymously(auth);
    const uid = cred.user.uid;
    // Mark as new user explicitly — onAuthStateChanged fired before we could
    // create the user doc, so refreshUserDetails may have already set this to
    // "false" via its empty-snapshot fallback. Override it here.
    localStorage.setItem(`newUserState_${uid}`, "true");
    // Create a minimal user doc so refreshUserDetails finds something
    const usersCol = collection(db, "users");
    const existing = await getDocs(query(usersCol, where("userId", "==", uid)));
    if (existing.empty) {
      // Build a unique display name so each guest is traceable in the admin panel.
      // Using the first 6 chars of the UID keeps it short but lookup-friendly.
      const shortId = uid.slice(0, 6);
      const installSource = getInstallSourceForUserDoc();
      await addDoc(usersCol, {
        userId: uid,
        name: `Guest ${shortId}`,
        email: null,
        isAnonymous: true,
        dateCreated: serverTimestamp(),
        // Persist the same defaults the client + cloud functions assume, so
        // the user doc is the single source of truth. Without this, the doc
        // has no `settings` field and admin tooling can't tell "off" from
        // "never toggled".
        settings: {
          notifications: true,
          notificationPrefs: DEFAULT_NOTIFICATION_PREFS,
        },
        ...(isLocalhost() ? { devAccount: true, devAccountReason: getDevAccountReason() } : {}),
        ...(Object.keys(installSource).length > 0 ? { installSource } : {}),
      });
    }
    // Re-fetch so userDetails reflects the freshly-created doc (overwriting
    // the empty fallback set by the race with onAuthStateChanged).
    await refreshUserDetails(cred.user);

    // Trigger geo capture explicitly after the user doc is committed. The
    // implicit call inside refreshUserDetails fires fire-and-forget and can
    // race the doc-creation index propagation; the delayed retry here gives
    // Firestore time to index the new doc before captureSignupGeo's query
    // looks for it.
    triggerGeoCaptureWithRetry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Convert the current anonymous account into a permanent email/password account.
   * Preserves the same UID so all existing data (notes, cycle logs, etc.) carries over.
   */
  const linkAnonymousToEmail = useCallback(async (email: string, password: string) => {
    if (!auth.currentUser) throw new Error("No active session to link.");
    if (!auth.currentUser.isAnonymous) throw new Error("Account is already permanent.");
    const credential = EmailAuthProvider.credential(email, password);
    await linkWithCredential(auth.currentUser, credential);
    // Persist the new email + flip the flag on the user doc
    if (userDocRef.current) {
      await updateDoc(userDocRef.current, {
        email,
        isAnonymous: false,
      });
    }
    // Mark this device as having had a real account so a subsequent sign-out
    // doesn't auto-create a new guest — they'll see the sign-in screen instead.
    localStorage.setItem("onyxlife_registered", "true");
    // Refresh details so the UI reflects the upgraded account
    await refreshUserDetails(auth.currentUser);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Convert the current anonymous account into a permanent Google-linked account.
   * Uses the native Google Auth plugin on Android/iOS, or a web popup otherwise.
   * Preserves the same UID so existing data carries over.
   */
  const linkAnonymousToGoogle = useCallback(async () => {
    if (!auth.currentUser) throw new Error("No active session to link.");
    if (!auth.currentUser.isAnonymous) throw new Error("Account is already permanent.");

    let linkedUser: User;
    // Hold the credential outside the try so the catch block can fall back to it
    // when GoogleAuthProvider.credentialFromError() can't extract it (which
    // happens on Capacitor's native flow — credentials we built ourselves don't
    // round-trip through the error object the same way as web popups).
    let nativeCredential: ReturnType<typeof GoogleAuthProvider.credential> | null = null;
    try {
      if (Capacitor.isNativePlatform()) {
        const { GoogleAuth } = await import("@codetrix-studio/capacitor-google-auth");
        const googleUser = await GoogleAuth.signIn();
        const idToken = googleUser.authentication?.idToken;
        if (!idToken) throw new Error("Google sign-in did not return an ID token.");
        nativeCredential = GoogleAuthProvider.credential(idToken);
        const result = await linkWithCredential(auth.currentUser, nativeCredential);
        linkedUser = result.user;
      } else {
        const result = await linkWithPopup(auth.currentUser, googleProvider);
        linkedUser = result.user;
      }
    } catch (err) {
      // `auth/credential-already-in-use` means the Google account is already
      // tied to a *different* Firebase user (usually an older sign-up). Firebase
      // can't merge accounts automatically. Best UX: sign into that existing
      // account, but first capture any onboarding the guest just completed and
      // copy it onto the existing user if they don't have onboarding yet —
      // otherwise they'd land in the app with no nav bar (showChrome requires
      // userDetails.onboarding).
      const code = (err as { code?: string })?.code;
      if (code === "auth/credential-already-in-use" || code === "auth/email-already-in-use") {
        const credentialFromErr = GoogleAuthProvider.credentialFromError(
          err as Parameters<typeof GoogleAuthProvider.credentialFromError>[0]
        );
        // Web popups give us the credential via credentialFromError; native
        // flows we built ourselves give us nativeCredential directly.
        const credential = credentialFromErr ?? nativeCredential;
        if (credential) {
          // Snapshot the guest's just-completed onboarding so we can transfer it.
          let guestOnboarding: unknown = null;
          if (userDocRef.current) {
            try {
              const guestSnap = await getDoc(userDocRef.current);
              if (guestSnap.exists()) {
                guestOnboarding = guestSnap.data().onboarding ?? null;
              }
            } catch {/* best-effort */}
          }

          const result = await signInWithCredential(auth, credential);

          // If the existing account has no onboarding, copy the guest's onto it
          // so they don't land into a chrome-less app with no nav bar.
          if (guestOnboarding) {
            try {
              const usersCol = collection(db, "users");
              const existingSnap = await getDocs(query(usersCol, where("userId", "==", result.user.uid)));
              if (!existingSnap.empty) {
                const existingDoc = existingSnap.docs[0];
                if (!existingDoc.data().onboarding) {
                  await updateDoc(existingDoc.ref, { onboarding: guestOnboarding });
                }
              }
            } catch (copyErr) {
              console.error("[linkAnonymousToGoogle] Failed to copy guest onboarding:", copyErr);
            }
          }

          localStorage.setItem("onyxlife_registered", "true");
          // Signal to AccountSwitchedNotice (mounted in App.tsx) that we did
          // a sign-into-existing fallback rather than a clean upgrade — it'll
          // show a one-time modal explaining what happened to the guest data.
          sessionStorage.setItem("onyxlife_account_switched", "1");
          await refreshUserDetails(result.user);
          return;
        }
      }
      throw err;
    }

    if (userDocRef.current) {
      await updateDoc(userDocRef.current, {
        email: linkedUser.email || null,
        name: linkedUser.displayName || "User",
        isAnonymous: false,
      });
    }
    // Mark this device as having had a real account so a subsequent sign-out
    // doesn't auto-create a new guest — they'll see the sign-in screen instead.
    localStorage.setItem("onyxlife_registered", "true");
    await refreshUserDetails(linkedUser);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateLoginRewards = useCallback(async (rewards: LoginRewards) => {
    if (!userDocRef.current) return;
    await updateDoc(userDocRef.current, { loginRewards: rewards });
    setUserDetails((prev) => prev ? { ...prev, loginRewards: rewards } : prev);
  }, []);

  useEffect(() => {
    refreshUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const contextValue = useMemo(() => ({
    userDetails,
    setUserDetails,
    refreshUserDetails,
    saveOnboarding,
    updateLoginRewards,
    authLoading,
    signInAsGuest,
    linkAnonymousToEmail,
    linkAnonymousToGoogle,
  }), [userDetails, setUserDetails, refreshUserDetails, saveOnboarding, updateLoginRewards, authLoading, signInAsGuest, linkAnonymousToEmail, linkAnonymousToGoogle]);

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextValue | null => useContext(UserContext);
